import React, { useState } from 'react';
import API from '../../api';
import { useCustomers } from '../context/CustomerContext';

export default function PaymentModal({ customer, onClose, onPaymentSuccess }) {
  const [receivedAmount, setReceivedAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [loading, setLoading] = useState(false);
  const { fetchCustomers } = useCustomers();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!receivedAmount || parseFloat(receivedAmount) <= 0) {
      alert('❌ Please enter a valid amount.');
      setLoading(false);
      return;
    }

    if (!paymentDate) {
      alert('❌ Please select a payment date.');
      setLoading(false);
      return;
    }

    try {
      await API.post('/payments/store', {
        customer_id: customer.id,
        received_amount: parseFloat(receivedAmount),
        payment_method: paymentMethod || '',
        payment_date: paymentDate,
      });

      alert('✅ Payment updated successfully!');
      if (onPaymentSuccess) onPaymentSuccess();
      onClose();
    } catch (err) {
      if (err.response?.data?.message) {
        alert(`❌ ${err.response.data.message}`);
      } else {
        alert('❌ Payment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-modal-title"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 id="payment-modal-title" className="text-lg font-semibold">
            Update Payment - {customer.customer_name}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close modal"
            className="text-gray-600 hover:text-gray-900 text-2xl font-bold"
          >
            &times;
          </button>
        </div>

        {/* Current Due Amount */}
        <div className="mb-4 font-semibold">
          Current Due Amount: ₹{customer.due_amount || 0}
        </div>

        {/* Received Amount */}
        <div className="mb-4">
          <label htmlFor="receivedAmount" className="block font-medium mb-1">
            Received Amount <span className="text-red-600">*</span>
          </label>
          <input
            id="receivedAmount"
            name="receivedAmount"
            type="number"
            step="0.01"
            min="0.01"
            value={receivedAmount}
            onChange={(e) => setReceivedAmount(e.target.value)}
            placeholder="Enter amount"
            required
            disabled={loading}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Payment Method */}
        <div className="mb-4">
          <label htmlFor="paymentMethod" className="block font-medium mb-1">
            Transaction ID / Cheque No.
          </label>
          <input
            id="paymentMethod"
            name="paymentMethod"
            type="text"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            placeholder="Optional"
            disabled={loading}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Payment Date */}
        <div className="mb-4">
          <label htmlFor="paymentDate" className="block font-medium mb-1">
            Payment Date <span className="text-red-600">*</span>
          </label>
          <input
            id="paymentDate"
            name="paymentDate"
            type="date"
            value={paymentDate}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
            disabled={loading}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Loading Spinner */}
        {loading && (
          <div className="flex items-center justify-center space-x-2 mb-4 text-gray-600">
            <svg
              className="animate-spin h-5 w-5 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <span>Processing payment...</span>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !receivedAmount || !paymentDate}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
          >
            {loading ? 'Processing...' : 'Submit Payment'}
          </button>
        </div>
      </form>
    </div>
  );
}
